import styled from "@emotion/styled"
import { css } from "@emotion/react"
import Color from "color"
import { darken } from "polished"

import TextareaAutosize from "react-autosize-textarea"

const LUM_THRESHOLD = 0.6
const ROUTER_BASE = process.env.REACT_APP_ROUTER_BASE || ""

export const Container = styled.div`
  width: 100%;
  height: 608px;
  font-family: sans-serif;
  overflow: hidden;
  position: relative;
  color: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return "#555"
    }
    return "#ffffff"
  }};

  >.content {
    top: 76px;
    left: 0;
    right: 0;
    bottom: 0;
    position: absolute;
    display: flex;
    flex-direction: column;
    font-size: 0.875rem;
  }
`

export const Header = styled.div`
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  box-sizing: border-box;
  background: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return props.color
    }
    return `linear-gradient(135deg, ${props.color} 0%, ${Color(props.color).darken(0.4).hsl().string()} 100%)`
  }};
  max-height: ${props => props.open ? "276px" : "76px"};
  transition: max-height 0.2s ease-in-out;
  box-shadow: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return "0 0px 8px 0 rgba(0, 0, 0, 0.1)"
    }
    return "0 0px 16px 0 rgba(0, 0, 0, 0.1)"
  }};
  overflow: ${props => props.showOverflow ? "visible" : "hidden"};
  z-index: 999998;
`

export const Tab = styled.div`
  padding: 8px;
  height: 100%;
  width: calc(100% / 2);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-sizing: border-box;
  border-right: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return "1px solid rgba(255, 255, 255, 0.1)"
    }
    return "1px solid rgba(0, 0, 0, 0.1)"
  }};
  cursor: pointer;
  color: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return "#555"
    }
    return "#ffffff"
  }};
  transition: background 0.2s linear;
  background: ${(props) => {
    if (props.selected) {
      const bg = Color(props.color)
      if (bg.luminosity() > LUM_THRESHOLD) {
        return "rgba(0, 0, 0, 0.03)"
      }
      return "rgba(255, 255, 255, 0.05)"
    }
    return "transparent"
  }};

  &:after {
    ${props => props.selected && css`
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: ${() => {
      const bg = Color(props.color)
      if (bg.luminosity() > LUM_THRESHOLD) {
        return "rgba(0, 0, 0, 0.2)"
      }
      return "rgba(255, 255, 255, 0.3)"
    }};
    `}
  }

  &:hover {
    background: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return "rgba(0, 0, 0, 0.03)"
    }
    return "rgba(255, 255, 255, 0.05)"
  }};
  }

  &:last-child {
    border-right: none;
  }

  .icon {
    font-size: 24px;
  }
`

export const FontSizeIcon = styled.div`
  width: 22px;
  height: 22px;
  background: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return `url('${ROUTER_BASE}/font_size_dark.png') center no-repeat`
    }
  return `url('${ROUTER_BASE}/font_size_light.png') center no-repeat`
  }};
  background-size: contain;
`

export const SettingsButton = styled.div`
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.2s linear;
  user-select: none;
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return "rgba(0, 0, 0, 0.05)"
    }
    return "rgba(255, 255, 255, 0.05)"
  }};

    .icon {
      animation-play-state: running;
    }
  }

  .icon {
    width: 16px;
    height: 16px;
    animation-play-state: paused;
    animation-duration: 4s;
    color: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return "#555"
    }
    return "#ffffff"
  }};
  }
`

export const ProfilePic = styled.div`
  width: ${(props) => props.size ? props.size : "40px"};
  height: ${(props) => props.size ? props.size : "40px"};
  border-radius: ${(props) => props.size ? `calc( ${props.size} / 2)` : "20px"};
  border: 2px solid ${props => darken(0.05, props.color)};
  box-sizing: border-box;
  background: white center center no-repeat url("${props => props.url}");
  background-size: cover;
  margin: 0 8px;
  cursor: pointer;

  &:hover {
    box-shadow: 0px 0px 8px 2px rgba(0, 0, 0, 0.05);
  }
`

export const ProfileContainer = styled.div`
  display: flex;
  align-items: center;

  >.name {
    cursor: pointer;
  }
`

export const BotProfileBar = styled.div`
  width: 100%;
  padding: 18px 16px;
  box-sizing: border-box;
  height: 76px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .align-right {
    display: flex;
    align-items: center;
  }
  .align-left {
    display: flex;
    align-items: center;
  }
`

export const BusinessInfoContainer = styled.div`
   width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    border-radius: 8px;
    box-sizing: border-box;
    background: ${(props) => {
      const bg = Color(props.color)
      if (bg.luminosity() > LUM_THRESHOLD) {
        return props.color
      }
      return `linear-gradient(135deg, ${props.color} 0%, ${Color(props.color).darken(0.4).hsl().string()} 100%)`
    }};
    height: 100%;
    transition: transform 1s ease-in-out 0.1s;
    transform: ${(props) => props.open ? "translateY(0px)" : "translateY(-100%)"};
    box-shadow: ${(props) => {
      const bg = Color(props.color)
      if (bg.luminosity() > LUM_THRESHOLD) {
        return "0 0px 8px 0 rgba(0, 0, 0, 0.1)"
      }
      return "0 0px 16px 0 rgba(0, 0, 0, 0.1)"
    }};
    overflow: ${(props) => props.open ? "visible" : "hidden"};
    z-index: 999999;
`

export const BusinessInfo = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  transition: transform 1s ease-in-out 0.05s;
  transform: ${(props) => props.open ? "translateY(0px)" : "translateY(-100%)"};
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

export const InfoContent = styled.div`
  padding: 16px;
  height: calc( 100% - 40px);
  overflow: scroll;

`

export const Settings = styled.div`
  width: 100%;
  box-sizing: border-box;
  height: 144px;
`

export const TabBar = styled.div`
  width: 100%;
  box-sizing: border-box;
  height: 44px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return "rgba(0, 0, 0, 0.03)"
    }
    return "rgba(255, 255, 255, 0.03)"
  }};
  box-shadow: 0px 2px 8px -2px rgba(0, 0, 0, 0.1);
`


export const LanguageIcon = styled.div`
  width: 24px;
  height: 24px;
  background: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return `url('${ROUTER_BASE}/language_dark.png') center no-repeat`
    }
    return `url('${ROUTER_BASE}/language_light.png') center no-repeat`
  }};
  background-size: contain;
`

export const InfoCard = styled.div`
  padding: 16px;
  margin-top: 16px;
  border-radius: 8px;
  display: flex;
  background: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return "#555"
    }
    return "rgb(255 255 255 / 20%)"
  }};
`

export const Info = styled.div`
  font-size: 0.8rem;
  padding-bottom: 4px;
  font-weight: ${(props) => props.bold && "bold"};
  word-break: break-word;

  &:last-child {
    padding-bottom: 0px;
  }

  >.separator {
    width: 100%;
    height: 1px;
    margin-top: 4px;
    background: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return "#555"
    }
    return "rgb(255 255 255 / 20%)"
    }};
  }

  &:last-child {
   .separator {
      display: none;
    }
  }
`

export const InfoWrapper = styled.div`
  font-size: 0.8rem;
  padding-bottom: 4px;

  &:last-child {
    padding-bottom: 0px;
  }
  >.separator {
    width: 100%;
    height: 1px;
    margin-top: 4px;
    margin-bottom: 4px;
    background: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return "#555"
    }
    return "rgb(255 255 255 / 20%)"
  }};
  }

  &:last-child {
   .separator {
     display: none;
   }
  }
`

export const Text = styled.span`
  word-break: break-word;
  flex-shrink: 1;
  flex-wrap: wrap;
`

export const IceBreakerButton = styled.div`
  color: ${(props) => props.themeColor};
  padding: 4px 8px;
  box-shadow:  0px 0px 8px 2px rgba(0, 0, 0, 0.07);
  display: inline-block;
  border-radius: 12px;
  margin: 8px;
`

export const Footer = styled.div`
  flex-shrink: 0;
  width: 100%;
  min-height: 88px;
  box-sizing: border-box;
  align-items: center;
  position: relative;
  bottom: 0;
  width: 100%;
  font-size: ${(props) => {
    if (props.fontSize === "12") {
      return "12px"
    } else if (props.fontSize === "16") {
      return "16px"
    } else if (props.fontSize === "22") {
      return "22px"
    }
  }};
  }};
`

export const CreateCustomerErrorContainer = styled.div`
  position: absolute;
  z-index: 99;
  width: 100%;
  height: 80px;
  top: -80px;
  right: 0;
  left: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding-bottom: 8px;
  overflow: hidden;
  pointer-events: none;
`

export const IceBreakersContainer = styled.div`
  position: absolute;
  z-index: 99;
  width: 100%;
  right: 0;
  left: 0;
  bottom: 72px;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  overflow: hidden;
  pointer-events: none;
  padding: 0 16px;
  flex-wrap: wrap;
  font-size: 0.9em;
`

export const TextField = styled(TextareaAutosize)`
  padding: 16px;
  /* padding-left: ${props => props["data-withpm"] ? "8px" : "28px"}; */
  flex-grow: 1;
  border: none;
  border-radius: 0;
  box-sizing: border-box;
  outline: none;
  font-size: 16px;
  resize: none;
  line-height: 1.33;
  font-family: sans-serif;
  -webkit-tap-highlight-color: transparent;
  background: ${(props) => props.theme.palerBackground} ;
  border-radius: 16px;
  height: 52px;
  margin-left: 16px;
  pointer-events: none;
`

export const UploadButton = styled.div`
  cursor: pointer;
  padding: 16px 0;
  margin: 0 8px 0 16px;
  user-select: none;
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  -webkit-tap-highlight-color: transparent;
  color: ${(props) => {
    if (props.disabled) {
      return "#ccc"
    }
    if (props.color) {
      const bg = Color(props.color)
      if (bg.luminosity() > LUM_THRESHOLD) {
        return "#0084FF"
      }
      return props.color
    }
    return "#0084FF"
  }};
  font-size: 20px;

  >label {
    cursor: pointer;
  }
`

export const SendButton = styled.div`
  cursor: pointer;
  padding: 16px 0;
  margin: 0 16px 0 8px;
  user-select: none;
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  -webkit-tap-highlight-color: transparent;
  color: ${(props) => {
    if (props.disabled) {
      return "#ccc"
    }
    if (props.color) {
      const bg = Color(props.color)
      if (bg.luminosity() > LUM_THRESHOLD) {
        return "#0084FF"
      }
      return props.color
    }
    return "#0084FF"
  }};
  font-size: 20px; 
`

export const ActionRow = styled.div`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  justify-content: center;
  display: flex;
  position: absolute;
  bottom: 0;

  >.action-wrapper {
    display: flex;
    padding: 8px 0px;
    align-items: center;
    width: 100%;
  }
`

export const Body = styled.div`
  flex-grow: 1;
  overflow-y: scroll;
  width: 100%;
  box-sizing: border-box;
  color: #000;

  ::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
`

export const Segment = styled.div`
  flex-grow: 1;
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 4px 0;
  border-right: 1px solid #fff;
  border-color: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return "#999"
    }
    return "#ffffff"
  }};

  &:last-child {
    border-right: none;
  }

  ${props => {
    const bg = Color(props.color)
    let color
    if (bg.luminosity() > LUM_THRESHOLD) {
      color = "#999"
    } else {
      color = "#ffffff"
    }

    if (props.selected) {
      return css`
        background-color: ${color};
      `
    }
  }}
`

export const SFont = styled.div`
  width: 24px;
  height: 24px;
  background: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return props.selected ? `url('${ROUTER_BASE}/s_font_light.png') center no-repeat` : `url('${ROUTER_BASE}/s_font_dark.png') center no-repeat`
    }
  return props.selected ? `url('${ROUTER_BASE}/s_font_dark.png') center no-repeat` : `url('${ROUTER_BASE}/s_font_light.png') center no-repeat`
  }};
  background-size: contain;
`

export const MFont = styled.div`
  width: 24px;
  height: 24px;
  background: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return props.selected ? `url('${ROUTER_BASE}/m_font_light.png') center no-repeat` : `url('${ROUTER_BASE}/m_font_dark.png') center no-repeat`
    }
  return props.selected ? `url('${ROUTER_BASE}/m_font_dark.png') center no-repeat` : `url('${ROUTER_BASE}/m_font_light.png') center no-repeat`
  }};
  background-size: contain;
`

export const LFont = styled.div`
  width: 24px;
  height: 24px;
  background: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return props.selected ? `url('${ROUTER_BASE}/l_font_light.png') center no-repeat` : `url('${ROUTER_BASE}/l_font_dark.png') center no-repeat`
    }
  return props.selected ? `url('${ROUTER_BASE}/l_font_dark.png') center no-repeat` : `url('${ROUTER_BASE}/l_font_light.png') center no-repeat`
  }};
  background-size: contain;
`

export const SegmentedControl = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #fff;
  border-radius: 6px;
  border-color: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return "#999"
    }
    return "#ffffff"
  }};
`

export const TabContent = styled.div`
  width: 100%;
  box-sizing: border-box;
  height: 100px;
  background: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return "rgba(0, 0, 0, 0)"
    }
    return "rgba(0, 0, 0, 0.05)"
  }};
  padding: 16px;
  display: flex;
  justify-content: center;
  /* align-items: center; */
  flex-direction: column;

  .field {
    .label {
      font-weight: 600;
      font-size: 0.9rem;
      margin-bottom: 8px;
    }

    .slider {
      width: 95%;
      margin-left: 2.5%;
    }

    .lang-selector {
      color: #000;
    }
  }
`